import { useEffect, useRef, useState } from 'react';
import { Modal, StatusBadge, Spinner, Field } from './ui';
import { sanitize } from '../lib/sanitize';
import { assetUrl } from '../api/client';
import { formatDate } from '../lib/format';
import { providerLabel } from '../lib/videoProvider';
import { useToast } from '../context/ToastContext';
import { SUBMISSION_TYPES, normalizeSubmission } from './submissionTypes';

/**
 * Full-content preview of a submission (story / news / activity).
 *
 * The body is rendered exactly the way the public detail pages render it:
 * `sanitize()` + `dangerouslySetInnerHTML` inside a `.rich-content` container,
 * so headings, lists, links, images and tables match the published design.
 *
 * Two audiences share this one component:
 *   - Administrators (Approval Center) get the reviewer note plus the
 *     Reject / Approve only / Approve & Publish actions.
 *   - Authors (member "My Stories / My News / My Activities") pass `readOnly`
 *     to get the same rendering with a single Close button, no reviewer note
 *     and no workflow actions.
 *
 * Props:
 *   item      normalized submission (see normalizeSubmission) or null
 *   onClose   close the preview
 *   onRefresh reload the queue + pending counts (review mode only)
 *   readOnly  hide every reviewer control (author preview)
 */
export default function SubmissionPreview({ item, onClose, onRefresh, readOnly = false }) {
  const toast = useToast();
  const [detail, setDetail] = useState(null); // full record fetched on open
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailFailed, setDetailFailed] = useState(false);
  const [comment, setComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(null); // 'approve' | 'publish' | 'reject'
  const noteRef = useRef(null);

  const meta = item ? SUBMISSION_TYPES[item.type] : null;

  // Reset per-item state whenever a different submission is opened.
  useEffect(() => {
    setDetail(null);
    setComment('');
    setRejecting(false);
    setBusy(null);
    setDetailFailed(false);
  }, [item?.key]);

  // The list endpoints already return the body/description, the featured image
  // and (activities + news) the gallery. Fetch the single record only when a
  // field the preview needs is missing from the list payload. The show endpoints are
  // owner-aware (StoryPolicy@view / the shared Approvable policy allow the
  // author of a non-published item), so this path works for members previewing
  // their own drafts exactly as it does for administrators.
  useEffect(() => {
    if (!item || !meta) return undefined;
    if (item.body) return undefined;
    let cancelled = false;
    setLoadingDetail(true);
    meta.api
      .get(item.slug || item.id)
      .then((full) => {
        if (!cancelled && full) setDetail(normalizeSubmission(full, item.type));
      })
      .catch(() => {
        // preview falls back to the list payload
        if (!cancelled) setDetailFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item, meta]);

  useEffect(() => {
    if (rejecting) noteRef.current?.focus();
  }, [rejecting]);

  const view = detail || item;

  async function run(kind) {
    if (!view || !meta || readOnly) return;
    const note = comment.trim();
    if (kind === 'reject' && !note) {
      toast.error('A reason is required before rejecting.');
      noteRef.current?.focus();
      return;
    }
    setBusy(kind);
    try {
      if (kind === 'reject') {
        const res = await meta.api.reject(view.id, note);
        toast.success(res?.message || `${meta.label} rejected.`);
      } else if (kind === 'approve') {
        const res = await meta.api.approve(view.id, note || undefined);
        toast.success(res?.message || `${meta.label} approved. Publish it when you are ready.`);
      } else {
        // publish is only valid from the `approved` state, so approve first and
        // then publish, carrying the same reviewer note into both records.
        await meta.api.approve(view.id, note || undefined);
        const res = await meta.api.publish(view.id, note || undefined);
        toast.success(res?.message || `${meta.label} approved and published.`);
      }
      onRefresh?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.message || 'The action could not be completed.');
      onRefresh?.();
    } finally {
      setBusy(null);
    }
  }

  const working = busy !== null;
  const typeLabel = meta ? meta.label.toLowerCase() : 'submission';
  const isPublished = view?.status === 'published';

  return (
    <Modal
      open={!!item}
      onClose={working ? () => {} : onClose}
      size="xl"
      title={
        meta
          ? `${readOnly ? 'Preview' : 'Review'} ${typeLabel}`
          : `${readOnly ? 'Preview' : 'Review'} submission`
      }
      footer={
        readOnly ? (
          <button className="btn-primary" onClick={onClose}>
            Close
          </button>
        ) : rejecting ? (
          <>
            <button className="btn-ghost" onClick={() => setRejecting(false)} disabled={working}>
              Cancel
            </button>
            <button
              className="btn-danger"
              onClick={() => run('reject')}
              disabled={working || !comment.trim()}
            >
              {busy === 'reject' ? 'Rejecting…' : 'Confirm rejection'}
            </button>
          </>
        ) : (
          <>
            <button className="btn-ghost mr-auto" onClick={onClose} disabled={working}>
              Close
            </button>
            <button
              className="btn-ghost text-red-600"
              onClick={() => setRejecting(true)}
              disabled={working}
            >
              Reject
            </button>
            <button className="btn-outline" onClick={() => run('approve')} disabled={working}>
              {busy === 'approve' ? 'Approving…' : 'Approve only'}
            </button>
            <button className="btn-primary" onClick={() => run('publish')} disabled={working}>
              {busy === 'publish' ? 'Publishing…' : 'Approve & Publish'}
            </button>
          </>
        )
      }
    >
      {view && meta && (
        <div className="space-y-6">
          {/* Status marker: the item's real workflow state, not an assumption */}
          <div className="flex flex-wrap items-center gap-2 rounded-xl bg-gold-50 px-4 py-3 ring-1 ring-gold-200">
            <span className="pill bg-gold-400 text-maroon-900">Preview</span>
            <StatusBadge status={view.status || 'pending'} />
            <span className="text-xs text-ink-soft">
              {isPublished
                ? `This is how the ${typeLabel} appears on the public site.`
                : `This is how the ${typeLabel} will look once published. It is not on the public site yet.`}
            </span>
          </div>

          {/* Hero, mirroring the public detail pages */}
          {view.image ? (
            <header className="relative overflow-hidden rounded-2xl">
              <img
                src={view.image}
                alt=""
                aria-hidden="true"
                className="h-56 w-full object-cover sm:h-72"
              />
              <div className="hero-overlay" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="eyebrow mb-2 text-gold-400">{meta.eyebrow}</p>
                <h2 className="font-serif text-2xl font-bold leading-tight text-white sm:text-3xl">
                  {view.title}
                </h2>
              </div>
            </header>
          ) : (
            <header className="rounded-2xl border border-cream-300 bg-cream-100 px-6 py-10 text-center">
              <p className="eyebrow mb-2 text-maroon-700">{meta.eyebrow}</p>
              <h2 className="heading-brand text-2xl sm:text-3xl">{view.title}</h2>
            </header>
          )}

          {/* Byline */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-ink-muted">
            {view.category && <span className="pill-gold">{view.category}</span>}
            {view.author && (
              <span>
                By <span className="font-semibold text-ink-soft">{view.author}</span>
              </span>
            )}
            {view.date && (
              <span>
                {view.dateLabel} {formatDate(view.date)}
              </span>
            )}
            <span className="text-xs uppercase tracking-wider text-ink-muted">
              {meta.label} #{view.id}
            </span>
          </div>

          {/* Excerpt / summary */}
          {view.excerpt && (
            <div className="rounded-2xl bg-cream-100 p-6 ring-1 ring-cream-300">
              <span aria-hidden="true" className="font-display text-3xl leading-none text-gold-500">
                “
              </span>
              <p className="mt-1 text-lg italic leading-relaxed text-ink-soft">{view.excerpt}</p>
            </div>
          )}

          {/* Body: same sanitize + .rich-content pipeline as the public pages */}
          {loadingDetail && !view.body ? (
            <div className="flex items-center gap-3 text-sm text-ink-muted">
              <Spinner className="h-5 w-5" />
              Loading the full submission…
            </div>
          ) : detailFailed && !view.body ? (
            <p className="rounded-xl border border-cream-300 bg-surface-alt p-4 text-sm text-ink-muted">
              The full content could not be loaded right now. Close this preview and try again.
            </p>
          ) : (
            <div
              className="rich-content"
              dangerouslySetInnerHTML={{
                __html: sanitize(view.body) || '<p>No content was submitted.</p>',
              }}
            />
          )}

          {/* Gallery (activities + news) */}
          {view.gallery?.length > 0 && (
            <section aria-labelledby="preview-gallery-heading">
              <h3 id="preview-gallery-heading" className="heading-brand text-lg">
                Gallery
              </h3>
              <p className="mt-1 text-sm text-ink-muted">
                {view.gallery.length} {view.gallery.length === 1 ? 'item' : 'items'} attached.
              </p>
              <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {view.gallery.map((g, i) => (
                  <li
                    key={g.id ?? i}
                    className="overflow-hidden rounded-xl bg-maroon-50 ring-1 ring-cream-300"
                  >
                    {g.type === 'image' && (
                      <img
                        src={assetUrl(g.url)}
                        alt={g.alt_text || `Attached photo ${i + 1}`}
                        loading="lazy"
                        className="aspect-square w-full object-cover"
                      />
                    )}
                    {g.type === 'video' && (
                      <video
                        src={assetUrl(g.url)}
                        controls
                        preload="metadata"
                        className="aspect-square w-full bg-black object-cover"
                      />
                    )}
                    {g.type === 'embed' && (
                      <a
                        href={g.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex aspect-square w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-maroon-800 to-maroon-950 p-3 text-center text-sm font-medium text-cream-100"
                      >
                        <span aria-hidden="true" className="text-2xl">
                          ▶
                        </span>
                        {providerLabel(g.provider)} link
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Reviewer note / rejection reason (review mode only) */}
          {!readOnly && (
            <div
              className={`rounded-2xl border p-5 ${
                rejecting ? 'border-red-300 bg-red-50' : 'border-cream-300 bg-surface-alt'
              }`}
            >
              <Field
                label={rejecting ? 'Reason for rejection (required)' : 'Reviewer note (optional)'}
                hint={
                  rejecting
                    ? 'The author sees this reason, so be specific about what needs to change.'
                    : 'Saved to the approval history alongside your decision.'
                }
              >
                <textarea
                  ref={noteRef}
                  className="input"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={
                    rejecting ? 'Explain what needs to change…' : 'Add a note for the audit trail…'
                  }
                />
              </Field>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
